import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys, TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../../../utils/tpoGenQueryKeys';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { sendNotification } from 'utils/helperMethods';
import { TPOGEN_EVENT_CREATED } from 'utils/mixpanelEventNames';
import { useEditWriteGuard } from '../../EditSession/useEditWriteGuard';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../utils/trackTpoGenEvent';

export interface CreateEventPayload {
    plan_id: string;
    event_id?: string | null;
    lowest_dim_attr_id: number[];
    [key: string]: unknown;
}

const createOrUpdateEvent = async (payload: CreateEventPayload & { created_by: string }) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.createOrUpdateEvent, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: payload,
    });
    return response.data;
};

const useCreateEventMutation = ({ onSuccess }: { onSuccess?: (data: any) => void } = {}) => {
    const queryClient = useQueryClient();
    const { refetchPromoOnEventUpdate } = useTpoGenConfig();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: CreateEventPayload) => {
            assertCanWrite();
            return createOrUpdateEvent({
                ...payload,
                event_id: payload.event_id ?? null,
                created_by: localStorage.getItem('email') ?? '',
            });
        },
        onSuccess: (_data, variables) => {
            const eventId = variables.event_id;
            if (eventId) {
                queryClient.invalidateQueries({ queryKey: tpoGenKeys.eventDetails(eventId as string) });
                queryClient.invalidateQueries({ queryKey: [TPO_GEN_DIM_OPTIONS_QUERY_KEY, 'product', eventId] });
            }
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            // Mark any open promo tabs' cached data as stale without an immediate
            // background refetch (refetchType: 'none'). Each promo tab re-fetches
            // lazily the next time the user opens it (see useGetPromoData).
            if (refetchPromoOnEventUpdate) {
                queryClient.invalidateQueries({
                    queryKey: [...tpoGenKeys.all, 'promo-details'],
                    refetchType: 'none',
                });
            }
            const isEdit = !!eventId;
            sendNotification('success', isEdit ? 'Event updated successfully' : 'Event created successfully');
            if (!isEdit) {
                // TODO(PM clarification): `source` is hardcoded to the CSV's fe_constant
                // 'plans'. Confirm whether event creation ever needs a second real value
                // here (e.g. an import-driven creation path), the way `creation_method`
                // on tpogen_plan_created turned out to need 'modal'/'planning_group'.
                trackTpoGenEvent(TPOGEN_EVENT_CREATED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                    event_id: _data?.event_id ?? _data?.eventId,
                    change_type: 'created',
                    source: 'plans',
                });
            }
            // `tpogen_event_updated` (edit case) is fired from EventsView's onSuccess
            // callback, which has the per-field diff needed for `change_type`.
            onSuccess?.(_data);
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to save event';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useCreateEventMutation;
